import { createAction } from "@reduxjs/toolkit";
import isEqual from "lodash/isEqual";
import { useSelector } from "react-redux";
import { call, put, select, takeEvery } from "redux-saga/effects";

import { updateSelectedAccounts } from "actions/commonActions";
import { SKED_CORE_GET_ACCOUNTS_ROUTINE } from "constants/ActionTypes";
import {
  getLocalStorageSelectedAccounts,
  setLocalStorageHomeSelectedGroupName,
} from "libs/storage/adapters";
import {
  addAccount,
  addAccounts,
  removeAccount,
  replaceAccount,
  replaceAllAccounts,
} from "../table";
import {
  getAccounts,
  getSelectedAccountIds,
  getSelectedAccounts,
} from "selectors/skedCoreSelectors";
import type { ReduxRootState } from "types/redux-store";
import type { CalendarPostType } from "features/calendar-new/types";
import type { AccountType } from "shared/types/accounts";

import {
  buildPostCreatedSelectionUpdate,
  deriveSelectedAccountIds,
} from "./selected-accounts.logic";

export const selectedAccountsPostsCreatedOrUpdated = createAction(
  "accounts/selectedAccountsPostsCreatedOrUpdated",
  function prepare(newPosts) {
    return {
      payload: {
        newPosts,
      },
    };
  }
);

export function* syncSelectedAccountsAfterAccountsLoad(action: {
  payload?: { accounts?: AccountType[] };
}) {
  const accounts: AccountType[] =
    action.payload?.accounts ?? (yield select(getAccounts));
  const reduxSelectedIds: string[] = yield select(getSelectedAccountIds);
  let selectedAccountIds = reduxSelectedIds;

  // On cold start (page refresh), Redux selectedIds is []. Check localStorage
  // for a previously-saved selection so the user's choice survives refresh
  // instead of resetting to the default.
  //
  // Distinguish a genuine cold start (no stored preference, `getLocalStorage…`
  // returns `null`) from an explicit user-cleared selection (the persistence
  // middleware writes the literal `"[]"` when the user deselects everything):
  //   - `null` / malformed / non-string array → fall back to the default group.
  //   - non-empty string array → restore it.
  //   - empty array `"[]"` → respect the explicit empty selection.
  // Without the last case, deselecting all accounts snaps back to the default
  // group on the next accounts-load saga run (see SKE-3741).
  let explicitEmpty = false;
  if (!selectedAccountIds.length) {
    const stored: string | null = yield call(getLocalStorageSelectedAccounts);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (
          Array.isArray(parsed) &&
          parsed.every((id: unknown) => typeof id === "string")
        ) {
          if (parsed.length > 0) {
            selectedAccountIds = parsed;
          } else {
            explicitEmpty = true;
          }
        }
      } catch {
        // Malformed localStorage — fall through to default selection
      }
    }
  }

  const nextSelectedAccountIds = explicitEmpty
    ? []
    : deriveSelectedAccountIds(accounts, selectedAccountIds);

  // Compare against the original Redux value, not the hydrated one,
  // so that localStorage-restored IDs are dispatched into the store.
  if (!isEqual(nextSelectedAccountIds, reduxSelectedIds)) {
    yield put(updateSelectedAccounts(nextSelectedAccountIds));
  }
}

export function* syncSelectedAccountsAfterPostsCreated(action: {
  payload: { newPosts: CalendarPostType[] };
}) {
  const accounts: AccountType[] = yield select(getAccounts);
  const selectedAccountIds: string[] = yield select(getSelectedAccountIds);
  const { nextSelectedAccountIds, homeSelectedGroupName } =
    buildPostCreatedSelectionUpdate({
      accounts,
      selectedAccountIds,
      newPosts: action.payload.newPosts,
    });

  if (!isEqual(nextSelectedAccountIds, selectedAccountIds)) {
    yield put(updateSelectedAccounts(nextSelectedAccountIds));
  }

  if (homeSelectedGroupName) {
    yield call(setLocalStorageHomeSelectedGroupName, homeSelectedGroupName);
  }
}

const accountMutationActionTypes = [
  SKED_CORE_GET_ACCOUNTS_ROUTINE.SUCCESS,
  replaceAllAccounts.type,
  addAccount.type,
  addAccounts.type,
  replaceAccount.type,
  removeAccount.type,
];

export function* watchSelectedAccountsBridgeRemoval() {
  yield takeEvery(
    accountMutationActionTypes,
    syncSelectedAccountsAfterAccountsLoad
  );
  yield takeEvery(
    selectedAccountsPostsCreatedOrUpdated,
    syncSelectedAccountsAfterPostsCreated
  );
}

export function useSelectedAccountIds(): string[] {
  return useSelector((state: ReduxRootState) => getSelectedAccountIds(state));
}

export function useSelectedAccounts(): AccountType[] {
  return useSelector((state: ReduxRootState) => getSelectedAccounts(state));
}
